import { Box, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { FONT_SIZE } from "../../../theme/tokens";
import { formatHppdMagnitude } from "./recommendationRow.utils";
import type { RecommendationId } from "./recommendations.constants";
import { hppdChangeCopy, moneyChangeCopy, pointsChangeCopy, pointsToneInk } from "./signedCopy.utils";

type Props = {
	recId: RecommendationId;
	// Theme-derived categorical accent for this lever (gains wear it).
	color: string;
	// Signed headline HPPD change on the lever's primary measure.
	impact: number;
	// Signed projected quarter dollars, priced from the whole hours committed (negative = savings).
	dollars: number;
	// Signed marginal points over the plan prefix, and their per-measure split (total-point units).
	points: number;
	breakdown: ReadonlyArray<{ categoryId: string; categoryTitle: string; hppdGap: number; points: number }>;
	// The slider sits left of 0 — reads in savings terms even when the dollars round to 0.
	isCut: boolean;
};

/**
 * The lever card's outcome column: the signed HPPD change, the priced cost ("$X total") or savings
 * ("Saves $X"), and the points it gains or gives up — with a per-measure tooltip when the lever moves
 * more than one HPPD measure (weekend levers move Weekend HPPD more than Total/RN, because the hours
 * only land on weekend shifts).
 */
export const RecommendationImpactSummary: FC<Props> = ({ recId, color, impact, dollars, points, breakdown, isCut }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const savingsInk = theme.palette.success.main;
	const hppdCopy = hppdChangeCopy(impact);
	const pointsCopy = pointsChangeCopy(points, isCut);
	const moneyCopy = moneyChangeCopy(dollars, isCut);
	const hasBreakdown = breakdown.length > 1;

	return (
		// One line per figure: the column is sized to its content (the row never shrinks it), so a long
		// figure widens the column rather than wrapping "−0.188 / HPPD" onto two lines.
		<Box sx={{ whiteSpace: "nowrap" }}>
			<Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>
				{t(hppdCopy.key, { value: formatHppdMagnitude(hppdCopy.magnitude) })}
			</Typography>
			{/* Per-rec projected dollars — mirrors ActionItems' per-item price, so the value here
			    matches the row in the Action Plan once the rec is added. */}
			<Typography
				variant="caption"
				sx={{ fontWeight: 600, display: "block", color: moneyCopy.isSavings ? savingsInk : "text.secondary" }}
				data-testid={`pbj-model-recommendation-cost-${recId}`}
			>
				{t(moneyCopy.key, { value: moneyCopy.value })}
			</Typography>
			<InHouseTooltip
				arrow
				placement="top"
				title={
					hasBreakdown ? (
						<Box sx={{ py: 0.25 }} data-testid={`pbj-model-recommendation-points-breakdown-${recId}`}>
							{breakdown.map((b) => {
								const gapCopy = hppdChangeCopy(b.hppdGap);
								const catPointsCopy = pointsChangeCopy(b.points, isCut);
								return (
									<Box key={b.categoryId} sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, fontSize: FONT_SIZE.LABEL_XS, lineHeight: 1.4 }}>
										<span>{b.categoryTitle}</span>
										<Box component="span" sx={{ fontWeight: 600 }}>
											{t(gapCopy.key, { value: gapCopy.magnitude.toFixed(3) })} · {t(catPointsCopy.key, catPointsCopy.values)}
										</Box>
									</Box>
								);
							})}
						</Box>
					) : (
						""
					)
				}
			>
				<Typography
					variant="caption"
					sx={{ fontWeight: "bold", color: pointsToneInk(theme, pointsCopy.tone, color), cursor: hasBreakdown ? "help" : "default" }}
					data-testid={`pbj-model-recommendation-points-${recId}`}
					data-points-tone={pointsCopy.tone}
				>
					{t(pointsCopy.key, pointsCopy.values)}
				</Typography>
			</InHouseTooltip>
		</Box>
	);
};
